import type { EscalationLevelInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, InputNumber } from 'antd';
import { useEffect } from 'react';
import type { EscalationLevel } from '../../../reference-data.api';

interface EscalationLevelFormDrawerProps {
  open: boolean;
  /** Present to edit that row; absent to create a new one. */
  editing?: EscalationLevel;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: EscalationLevelInput) => Promise<boolean>;
  onClose: () => void;
}

export function EscalationLevelFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: EscalationLevelFormDrawerProps) {
  const [form] = Form.useForm<EscalationLevelInput>();

  useEffect(() => {
    if (open) form.setFieldsValue(editing ?? { label: '', daysThreshold: undefined });
  }, [open, editing, form]);

  async function submit(values: EscalationLevelInput) {
    const saved = await onSubmit(values);
    if (saved) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit escalation level' : 'New escalation level'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<EscalationLevelInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Enter a label' }]}>
          <Input autoFocus />
        </Form.Item>
        <Form.Item
          name="daysThreshold"
          label="Days before the deadline"
          extra="Zero is the deadline day; a negative number is days past it, for an overdue level."
          rules={[{ required: true, message: 'Enter a number of days' }]}
        >
          <InputNumber className="w-full" precision={0} min={-365} max={365} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create escalation level'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
